import { Link, useLocation, useParams } from "react-router-dom";
import { Plane } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, SectionHead } from "@/components/layout/Motion";
import { CallCta, PaymentOptions } from "@/components/layout/CtaBlocks";
import { RouteSections } from "@/components/home/RouteSections";
import { AIRLINES, DESTINATIONS, NAV, NAV_2, MORE_WAYS, BRAND, COMPANY, EMAIL, PHONE_DISPLAY } from "@/lib/site";

const Prose = ({ children }) => <section className="bg-white py-14"><Reveal className="mx-auto max-w-3xl space-y-4 px-4 text-slate-700 sm:px-8">{children}</Reveal></section>;

export const AirlinePage = () => {
  const { slug } = useParams();
  const a = AIRLINES.find((x) => x.slug === slug) ?? AIRLINES[0];
  return (
    <Layout>
      <PageHero testId="airline-hero" compact crumb={a.name} eyebrow="Airline flight deals" icon={Plane} line1={`${a.name}`} line2="flight deals." text={`Ask our travel specialists to check available ${a.name} fares for your route and dates.`}>
        <div className="mt-8 inline-grid h-20 w-44 place-items-center rounded-xl bg-white p-3"><img src={a.logo} alt={`${a.name} logo`} className="max-h-12" /></div>
      </PageHero>
      <Prose>
        <p>{a.name} serves a wide network of routes. Fares, baggage rules and seat availability change often, so the fastest way to compare options is a quick call with our team.</p>
        <p>We’ll compare {a.name} with other carriers on your route and explain the total fare before you book. {BRAND} is an independent agency and is not affiliated with {a.name}.</p>
      </Prose>
      <CallCta />
      <PaymentOptions />
    </Layout>
  );
};

export const DestinationPage = () => {
  const { slug } = useParams();
  const d = DESTINATIONS.find((x) => x.slug === slug) ?? DESTINATIONS[0];
  return (
    <Layout>
      <PageHero testId="destination-hero" image={d.image} eyebrow="Flights to" icon={Plane} line1={`Flights to ${d.name}.`} line2="One call away." text={d.blurb} />
      <RouteSections />
      <CallCta title={[`Thinking about ${d.name}?`, "Let’s talk travel."]} />
    </Layout>
  );
};

const SERVICES = {
  "/group-travel": ["Group travel", "Travelling together?", "Weddings, reunions, sports teams and tours — we coordinate seats, fares and timings for groups of 6 or more."],
  "/business-travel": ["Business travel", "Travel that works as hard as you do.", "Flexible tickets, last-minute changes and one point of contact for your team’s trips."],
  "/student-travel": ["Student travel", "Heading off to study?", "Help with one-way fares, extra baggage and flexible dates for students and visiting families."],
};

export const ServicePage = () => {
  const { pathname } = useLocation();
  const [crumb, title, text] = SERVICES[pathname] ?? SERVICES["/group-travel"];
  return (
    <Layout>
      <PageHero testId="service-hero" compact crumb={crumb} line1={title} text={text} />
      <Prose><p>Tell us about your plans and a specialist will compare options across airlines and dates. Every itinerary is reviewed with you before you pay.</p></Prose>
      <CallCta />
    </Layout>
  );
};

export const SiteMap = () => (
  <Layout>
    <PageHero testId="sitemap-hero" compact crumb="Site map" line1="All destinations & airlines" />
    <section className="bg-white py-14">
      <div className="mx-auto grid max-w-[1320px] gap-10 px-4 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
        {[["Pages", [...NAV, ...NAV_2].map((n) => [n.to, n.label])], ["Airlines", AIRLINES.map((a) => [`/airlines/${a.slug}`, a.name])], ["Destinations", DESTINATIONS.map((d) => [`/flights-to/${d.slug}`, d.name])], ["More", [...MORE_WAYS.map((m) => [m.to, m.label]), ["/privacy", "Privacy policy"], ["/terms", "Terms & conditions"]]]].map(([t, links]) => (
          <div key={t}><SectionHead title={t} /><ul className="mt-4 space-y-2 text-sm">{links.map(([to, l]) => <li key={to}><Link to={to} className="text-slate-700 hover:text-brand">{l}</Link></li>)}</ul></div>
        ))}
      </div>
    </section>
  </Layout>
);

const LEGAL = {
  "/privacy": ["Privacy policy", [`${BRAND} is operated by ${COMPANY}. This policy explains how we handle information you share when you call, email or use this website.`, "We collect the details needed to search and book travel for you, such as names, contact details, travel dates and payment information, and use them only to provide and support your booking.", "We share traveler details with airlines and travel suppliers strictly to complete your reservation. We do not sell your personal information.", "We use cookies to improve your browsing experience and understand site usage. You can control cookies in your browser settings.", `To ask about or update your information, email ${EMAIL} or call ${PHONE_DISPLAY}.`]],
  "/terms": ["Terms & conditions", [`These terms govern services provided by ${BRAND}, operated by ${COMPANY}. By booking with us you agree to these terms and to the rules of the airline or supplier you travel with.`, "Fares shown on this website are indicative and not guaranteed. The final price, availability and fare rules are confirmed by our agent before payment.", "Changes, cancellations and refunds are subject to airline fare rules and may involve airline penalties and agency service fees, disclosed before you agree.", "Travelers are responsible for valid passports, visas and health documents required for their journey.", `${BRAND} is an independent travel agency and is not an airline. Airline names and trademarks belong to their respective owners.`]],
};

export const LegalPage = () => {
  const { pathname } = useLocation();
  const [title, paras] = LEGAL[pathname] ?? LEGAL["/privacy"];
  return (
    <Layout>
      <PageHero testId="legal-hero" compact crumb={title} line1={title} />
      <Prose>{paras.map((p) => <p key={p}>{p}</p>)}</Prose>
    </Layout>
  );
};
